import { useEffect, useState } from "react";

// Pure-SVG vector technician with magnifying glass scanning a sheet.
// Lightweight, vector-crisp, and stays on-brand at any size.
// Branding: Kolt navy (#333F48), amber (#DD6B33), slate paper.
export default function TechnicianAnimation({ size = 240, label = "Crafting for detail…", progress }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => (t + 1) % 100), 80);
    return () => clearInterval(id);
  }, []);

  // Pseudo progress messages cycled while the proof is being generated.
  const messages = [
    "Initialising pre-flight engine…",
    "Verifying colour space (CMYK)…",
    "Embedding ICC profile…",
    "Generating crop & bleed marks…",
    "Rendering 1:1 proof…",
    "Crafting for detail…",
  ];
  const msg = label || messages[Math.floor(tick / 18) % messages.length];

  return (
    <div
      className="flex flex-col items-center text-center select-none"
      data-testid="technician-animation"
    >
      <svg
        viewBox="0 0 240 240"
        width={size}
        height={size}
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Kolt Printing technician reviewing your file"
      >
        <defs>
          <clipPath id="paperClip">
            <rect x="40" y="56" width="120" height="148" rx="3" />
          </clipPath>
          <linearGradient id="lensGloss" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
            <stop offset="55%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Paper sheet */}
        <rect x="40" y="56" width="120" height="148" rx="3" fill="#FAFAF7" stroke="#CBD5E1" strokeWidth="1.5" />
        {/* Faint print rules on paper */}
        <g stroke="#E2E8F0" strokeWidth="1">
          <line x1="52" y1="78" x2="148" y2="78" />
          <line x1="52" y1="92" x2="148" y2="92" />
          <line x1="52" y1="106" x2="120" y2="106" />
          <line x1="52" y1="120" x2="148" y2="120" />
          <line x1="52" y1="134" x2="138" y2="134" />
          <line x1="52" y1="148" x2="120" y2="148" />
          <line x1="52" y1="162" x2="148" y2="162" />
        </g>
        {/* Brand chip on paper */}
        <rect x="52" y="64" width="18" height="6" rx="1" fill="#333F48" />
        <rect x="74" y="64" width="34" height="6" rx="1" fill="#DD6B33" />

        {/* Scanning beam under the glass */}
        <g clipPath="url(#paperClip)">
          <rect
            x="44"
            y={66 + (tick % 60) * 2}
            width="112"
            height="6"
            fill="#DD6B33"
            opacity="0.18"
          />
        </g>

        {/* Technician — minimalist torso/head silhouette */}
        <g>
          {/* Body */}
          <path
            d="M30 240 Q30 196 70 196 L96 196 Q120 196 120 220 L120 240 Z"
            fill="#333F48"
          />
          {/* Apron */}
          <path d="M58 240 L58 210 L92 210 L92 240 Z" fill="#0B5DA0" />
          <line x1="75" y1="210" x2="75" y2="240" stroke="#003766" strokeWidth="1" />
          {/* Head */}
          <circle cx="84" cy="180" r="16" fill="#F5D7B6" />
          {/* Hair */}
          <path d="M68 178 Q70 162 84 162 Q100 162 100 178 L100 173 Q92 168 84 168 Q72 170 68 178 Z" fill="#1E293B" />
          {/* Glasses */}
          <circle cx="80" cy="182" r="3" fill="none" stroke="#1E293B" strokeWidth="1" />
          <circle cx="90" cy="182" r="3" fill="none" stroke="#1E293B" strokeWidth="1" />
          <line x1="83" y1="182" x2="87" y2="182" stroke="#1E293B" strokeWidth="1" />
          {/* Mouth */}
          <path d="M81 189 Q84 191 87 189" stroke="#1E293B" strokeWidth="1" fill="none" strokeLinecap="round" />
          {/* Collar */}
          <path d="M70 198 L84 206 L98 198" stroke="#003766" strokeWidth="1.5" fill="none" />
        </g>

        {/* Arm + magnifying glass.  Translates vertically to mimic scanning. */}
        <g
          style={{
            transform: `translate(${Math.sin(tick / 6) * 12}px, ${(tick % 60) * 1.4}px)`,
            transition: "transform 80ms linear",
          }}
        >
          {/* Arm */}
          <path
            d="M104 200 Q126 168 152 124"
            stroke="#333F48"
            strokeWidth="10"
            fill="none"
            strokeLinecap="round"
          />
          {/* Cuff */}
          <circle cx="152" cy="124" r="6.5" fill="#003766" />
          {/* Handle */}
          <rect
            x="146"
            y="92"
            width="6"
            height="34"
            rx="3"
            fill="#DD6B33"
            transform="rotate(35 149 109)"
          />
          {/* Lens */}
          <circle cx="172" cy="88" r="22" fill="#E0F2FE" fillOpacity="0.55" stroke="#1E293B" strokeWidth="3" />
          <circle cx="172" cy="88" r="22" fill="url(#lensGloss)" />
          {/* Tiny crosshair inside the lens */}
          <line x1="160" y1="88" x2="184" y2="88" stroke="#333F48" strokeWidth="0.7" opacity="0.65" />
          <line x1="172" y1="76" x2="172" y2="100" stroke="#333F48" strokeWidth="0.7" opacity="0.65" />
        </g>

        {/* Glint */}
        <circle
          cx="166"
          cy="80"
          r="3"
          fill="#ffffff"
          opacity={0.4 + 0.6 * Math.abs(Math.sin(tick / 4))}
        />
      </svg>

      <div className="mt-4 text-sm font-semibold text-slate-800 font-display tracking-tight">
        Kolt Quality Control
      </div>
      <div className="text-xs text-slate-500 mt-1 min-h-[1rem]" data-testid="technician-status">
        {msg}
      </div>
      {typeof progress === "number" && (
        <div className="mt-3 w-48 h-1 bg-slate-200 rounded overflow-hidden">
          <div
            className="h-full bg-[#DD6B33] transition-all duration-300"
            style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
          />
        </div>
      )}
      <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-bold mt-3">
        Custom printing you&apos;ll actually love
      </div>
    </div>
  );
}
